import { IStyles } from '../../../commonUtils/styleUtils';
import { NoContentCard } from '../NoContent.tsx';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import { MouseEvent, useEffect, useState } from 'react';
import { GIL, gilTableText } from './gilTheme';
import { GalleryMedia, resolveMediaUrl } from './utils';

/**
 * Resolves each upload's real url once the items are on screen. Until it
 * settles the best-guess url is used, so nothing flashes empty.
 */
const useResolvedUrls = (items: GalleryMedia[]) => {
  const [urls, setUrls] = useState<Record<string, string>>({});
  //~ Re-runs when the set of items changes, not on every render.
  const signature = items.map((item) => item.key).join('|');

  useEffect(() => {
    let active = true;
    (async () => {
      const resolved = await Promise.all(
        items.map(async (item) => [item.key, await resolveMediaUrl(item)])
      );
      if (active) {
        setUrls(Object.fromEntries(resolved));
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return (item: GalleryMedia) => urls[item.key] || item.url;
};

const INITIAL_VISIBLE = 8;
export const NO_EVIDENCE_TEXT = 'No evidence uploaded';

/** Thumbnails follow the Lab's group cards; the viewer follows ProjectEvidenceDialog. */
const styles: IStyles = {
  grid: {
    display: 'grid',
    // The theme's sm starts at 300px, so phones stay on two columns.
    gridTemplateColumns: {
      xs: 'repeat(2, minmax(0, 1fr))',
      md: 'repeat(3, minmax(0, 1fr))',
      lg: 'repeat(4, minmax(0, 1fr))',
    },
    gap: { xs: pxToRem(12), md: pxToRem(18), lg: pxTovW(20) },
  },
  tile: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
    width: '100%',
    padding: 0,
    backgroundColor: '#FFFFFF',
    border: '1px solid #E0DFDE',
    borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
    overflow: 'hidden',
    cursor: 'pointer',
    font: 'inherit',
    '&:hover': { borderColor: '#0AA34F' },
    '&:focus-visible': { outline: '2px solid #0AA34F', outlineOffset: 2 },
  },
  thumb: {
    position: 'relative',
    width: '100%',
    aspectRatio: '4 / 3',
    backgroundColor: '#F4F7FB',
  },
  thumbMedia: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  playIcon: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: 'rgba(255,255,255,0.92)',
    fontSize: { xs: pxToRem(36), md: pxToRem(40), lg: pxTovW(56) },
    filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))',
  },
  captionBox: {
    padding: {
      xs: `${pxToRem(8)} ${pxToRem(10)}`,
      md: `${pxToRem(10)} ${pxToRem(14)}`, lg: `${pxTovW(10)} ${pxTovW(14)}`,
    },
    minWidth: 0,
  },
  modal: {
    '& .MuiDialog-paper': {
      width: { xs: '92vw', md: '80vw', lg: pxTovW(860) },
      m: { xs: '4vw', md: pxToRem(32) },
      maxWidth: 'none',
      borderRadius: { xs: pxToRem(20), md: pxToRem(18), lg: pxTovW(20) },
      p: { xs: pxToRem(16), md: pxToRem(21), lg: pxTovW(24) },
      boxSizing: 'border-box',
    },
  },
  mediaBox: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F4F7FB',
    borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
    minHeight: { xs: pxToRem(180), md: pxToRem(220), lg: pxTovW(260) },
    p: { xs: pxToRem(10), md: pxToRem(12), lg: pxTovW(12) },
  },
  media: {
    maxWidth: '100%',
    maxHeight: '60vh',
    objectFit: 'contain',
    borderRadius: { xs: pxToRem(8), md: pxToRem(8), lg: pxTovW(8) },
  },
  navButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E0DFDE',
    '&:hover': { backgroundColor: '#FFFFFF' },
  },
  outlinedGreenButton: {
    flexShrink: 0,
    textTransform: 'none',
    fontFamily: GIL.tableFont,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    color: '#0AA34F',
    border: '1px solid #0AA34F',
    backgroundColor: 'common.white',
    borderRadius: { xs: pxToRem(6), md: pxToRem(6), lg: pxTovW(6) },
    paddingX: { xs: pxToRem(10), md: pxToRem(14), lg: pxTovW(14) },
    paddingY: { xs: pxToRem(4), md: pxToRem(4), lg: pxTovW(4) },
    fontSize: { xs: pxToRem(12), md: pxToRem(14), lg: pxTovW(14) },
    '&:hover': {
      backgroundColor: 'common.white',
      border: '1px solid #0AA34F',
      boxShadow: 'none',
    },
  },
};

interface ViewerProps {
  items: GalleryMedia[];
  /** Open while set; the item shown first. */
  index?: number;
  onIndexChange: (index: number | undefined) => void;
  /** Whose evidence this is, e.g. a student or group name. */
  title?: string;
}

/** Full-size photo / video viewer with previous and next across `items`. */
export const MediaViewerDialog = ({
  items,
  index,
  onIndexChange,
  title,
}: ViewerProps) => {
  const urlOf = useResolvedUrls(items);
  const current = index !== undefined ? items[index] : undefined;
  const step = (delta: number) =>
    index !== undefined &&
    onIndexChange((index + delta + items.length) % items.length);

  return (
    <Dialog
      open={!!current}
      onClose={() => onIndexChange(undefined)}
      sx={styles.modal}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight') step(1);
        if (event.key === 'ArrowLeft') step(-1);
      }}
    >
      {current && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: pxToRem(14), md: pxToRem(16), lg: pxTovW(16) },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: pxToRem(10),
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h2" fontWeight="bold">
                Project Evidence
              </Typography>
              <Typography variant="subText" color="#9F9F9F">
                {[title, current.caption, current.subCaption]
                  .filter(Boolean)
                  .join(' | ') || current.fileName}
              </Typography>
            </Box>
            <IconButton
              aria-label="Close"
              size="small"
              onClick={() => onIndexChange(undefined)}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={styles.mediaBox}>
            {current.isVideo ? (
              <Box
                component="video"
                key={current.key}
                src={urlOf(current)}
                controls
                autoPlay
                playsInline
                sx={{ ...styles.media, width: '100%' }}
              />
            ) : (
              <Box
                component="img"
                src={urlOf(current)}
                alt={current.fileName}
                sx={styles.media}
              />
            )}
            {items.length > 1 && (
              <>
                <IconButton
                  aria-label="Previous"
                  size="small"
                  onClick={() => step(-1)}
                  sx={{ ...styles.navButton, left: pxToRem(10) }}
                >
                  <ChevronLeftRoundedIcon />
                </IconButton>
                <IconButton
                  aria-label="Next"
                  size="small"
                  onClick={() => step(1)}
                  sx={{ ...styles.navButton, right: pxToRem(10) }}
                >
                  <ChevronRightRoundedIcon />
                </IconButton>
              </>
            )}
          </Box>

          {items.length > 1 && (
            <Typography
              variant="subText"
              color="#9F9F9F"
              sx={{ textAlign: 'center' }}
            >
              {`${(index ?? 0) + 1} of ${items.length}`}
            </Typography>
          )}
        </Box>
      )}
    </Dialog>
  );
};

/** Grey "No evidence uploaded" line, for a table cell or a row without uploads. */
export const NoEvidence = () => (
  <Typography noWrap sx={gilTableText(14, 400, GIL.cellSub)}>
    {NO_EVIDENCE_TEXT}
  </Typography>
);

/** Whole-section empty state, the same card as "No Active Projects". */
export const NoEvidenceCard = () => (
  <NoContentCard variant="info" icon="laboratory" text={NO_EVIDENCE_TEXT} />
);

/**
 * One row's evidence: a "View Evidence (n)" button that opens that row's own
 * uploads, or "No evidence uploaded". Clicks never reach the row underneath,
 * which navigates elsewhere.
 */
export const EvidenceButton = ({
  items,
  title,
}: {
  items: GalleryMedia[];
  title?: string;
}) => {
  const [openIndex, setOpenIndex] = useState<number>();

  if (items.length === 0) {
    return <NoEvidence />;
  }
  return (
    <>
      <Button
        variant="outlined"
        sx={styles.outlinedGreenButton}
        onClick={(event: MouseEvent) => {
          event.stopPropagation();
          setOpenIndex(0);
        }}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {`View Evidence (${items.length})`}
      </Button>
      <MediaViewerDialog
        items={items}
        index={openIndex}
        onIndexChange={setOpenIndex}
        title={title}
      />
    </>
  );
};

/** Grid of one student's or group's photos and videos; the empty-state card when there are none. */
export const GilGallery = ({
  items,
  title,
}: {
  items: GalleryMedia[];
  title?: string;
}) => {
  const [showAll, setShowAll] = useState(false);
  const [openIndex, setOpenIndex] = useState<number>();
  const urlOf = useResolvedUrls(items);

  if (items.length === 0) {
    return <NoEvidenceCard />;
  }

  const visible = showAll ? items : items.slice(0, INITIAL_VISIBLE);

  return (
    <>
      <Box sx={styles.grid}>
        {visible.map((item, index) => (
          <Box
            key={item.key}
            component="button"
            type="button"
            sx={styles.tile}
            onClick={() => setOpenIndex(index)}
            aria-label={`Open ${item.fileName || 'media'}`}
          >
            <Box sx={styles.thumb}>
              {item.isVideo ? (
                <>
                  <Box
                    component="video"
                    src={`${urlOf(item)}#t=0.5`}
                    preload="metadata"
                    muted
                    playsInline
                    sx={styles.thumbMedia}
                  />
                  <PlayCircleFilledRoundedIcon sx={styles.playIcon} />
                </>
              ) : (
                <Box
                  component="img"
                  src={urlOf(item)}
                  alt={item.fileName}
                  loading="lazy"
                  sx={styles.thumbMedia}
                />
              )}
            </Box>
            {(item.caption || item.subCaption) && (
              <Box sx={styles.captionBox}>
                {item.caption && (
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    noWrap
                    component="p"
                  >
                    {item.caption}
                  </Typography>
                )}
                {item.subCaption && (
                  <Typography
                    variant="subText"
                    color="#9F9F9F"
                    noWrap
                    component="p"
                  >
                    {item.subCaption}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {items.length > INITIAL_VISIBLE && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: { xs: pxToRem(16), md: pxToRem(18), lg: pxTovW(20) },
          }}
        >
          <Button
            variant="outlined"
            sx={{ ...styles.outlinedGreenButton, paddingX: pxToRem(16) }}
            onClick={() => setShowAll((value) => !value)}
          >
            {showAll ? 'Show Less' : `View All (${items.length})`}
          </Button>
        </Box>
      )}

      <MediaViewerDialog
        items={items}
        index={openIndex}
        onIndexChange={setOpenIndex}
        title={title}
      />
    </>
  );
};

export default GilGallery;
